import AppContext from 'App/AppContext';
import { IPFontIcon, IPLabel, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import TimePicker from 'components/Pickers/TimePicker';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapLargePlus5,
  globalStackTokensGapMedium,
  globalTextStylesError,
} from 'globalStyles';
import Task from 'models/tasks/task';
import { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateLong, toLocaleTimeNoSeconds } from 'utils/datetime';
import { useIsInTaskDetailCallout } from './TaskDetailRow';
import { iconFontSize, readOnlyRowSpace } from './TaskDetailStyles';

interface ITaskDetailStartProps {
  orgTask: Task;
  task: Task;
  canUpdate: boolean;
  onUpdate: (newTask: Task) => void;
  getErrorMessage: (field: string) => string | undefined;
  loadRescheduleRange?: () => void;
  hideEndTime?: boolean;
}

export const TaskDetailStart = (props: ITaskDetailStartProps) => {
  const isInCallout = useIsInTaskDetailCallout();
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation']);
  const isReadonly = !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Start, appContext);

  //
  // Main render
  //
  return (
    <IPStack
      horizontal
      tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
      verticalAlign="start"
      styles={isReadonly ? { root: { paddingBottom: readOnlyRowSpace } } : undefined}
    >
      {!isInCallout && (
        <IPStack.Item>
          {!appContext.isMobileView && <IPFontIcon iconName="ReminderTime" style={iconFontSize} />}
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <IPStack
          horizontal
          //in a callout the date gets a row of its own, the times follow on the next one
          wrap={isInCallout}
          tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
        >
          {isReadonly && (
            <Fragment>
              <IPStack.Item styles={{ root: { width: '50%' } }}>
                <IPLabel>{t('task:Start.Label')}</IPLabel>
                <IPText>{toLocaleDateLong(props.task.startDateTime)}</IPText>
              </IPStack.Item>
              <IPStack.Item styles={{ root: { width: '50%' } }}>
                <IPStack horizontal tokens={globalStackTokensGapLargePlus5} horizontalAlign="start">
                  <IPStack.Item>
                    <IPLabel>{t('task:Time.Label')}</IPLabel>
                    <IPText>{toLocaleTimeNoSeconds(props.task.startDateTime)}</IPText>
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPLabel>{t('task:End.Label')}</IPLabel>
                    <IPText>{toLocaleTimeNoSeconds(props.task.endDateTime)}</IPText>
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
            </Fragment>
          )}
          {!isReadonly && (
            <Fragment>
              <IPStack.Item grow styles={isInCallout ? { root: { width: '100%' } } : undefined}>
                <LocalizedDatePicker
                  label={props.task.isRecurring() ? t('task:Start.LabelRecurring') : t('task:Start.Label')}
                  showGoToToday={true}
                  showWeekNumbers={true}
                  value={props.task.startDateTime}
                  onDateChange={(newDate) => {
                    if (newDate) {
                      const copyTask = props.task.clone();
                      copyTask.setNewStartDate(newDate);
                      if (props.loadRescheduleRange) props.loadRescheduleRange();
                      props.onUpdate(copyTask);
                    }
                  }}
                  disabled={isReadonly}
                />
                <IPText variant="small" styles={globalTextStylesError}>
                  {props.getErrorMessage('endDateTime')}
                </IPText>
                <IPText variant="small" styles={globalTextStylesError}>
                  {props.getErrorMessage('startDateTime')}
                </IPText>
              </IPStack.Item>
              <IPStack.Item>
                <TimePicker
                  selectedTime={props.task.startDateTime}
                  label={t('task:Time.Label')}
                  onUpdateTime={(hours: number, mins: number) => {
                    const copyTask = props.task.clone();
                    copyTask.setNewStartTime(hours, mins);
                    if (props.loadRescheduleRange) props.loadRescheduleRange();
                    props.onUpdate(copyTask);
                  }}
                  disabled={isReadonly}
                />
              </IPStack.Item>
              {!props.hideEndTime && (
                <IPStack.Item>
                  <TimePicker
                    selectedTime={props.task.endDateTime}
                    label={t('task:End.Label')}
                    onUpdateTime={(hours: number, mins: number) => {
                      const copyTask = props.task.clone();
                      copyTask.setNewEndTime(hours, mins);
                      if (props.loadRescheduleRange) props.loadRescheduleRange();
                      props.onUpdate(copyTask);
                    }}
                    disabled={isReadonly}
                  />
                </IPStack.Item>
              )}
            </Fragment>
          )}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};
